import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const relatedArticles = useMemo(
    () =>
      article
        ? articles
            .filter((a) => a.category === article.category && a.slug !== article.slug)
            .slice(0, 3)
        : [],
    [article]
  );

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
            <i className="ri-file-unknow-line text-4xl"></i>
          </span>
          <h2 className="text-xl font-heading font-semibold text-foreground-700 mb-2">文章未找到</h2>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
        </div>
      </div>
    );
  }

  const categoryLabel =
    article.category === 'plot-analysis' ? '剧情解析' :
    article.category === 'characters' ? '角色介绍' :
    article.category === 'guides' ? '游戏攻略' :
    article.category === 'merchandise' ? '周边资讯' : '粉丝社区';

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    articleSection: categoryLabel,
    keywords: article.tags.join(', '),
  };

  return (
    <main className="min-h-screen pt-16">
      <script type="application/ld+json">{JSON.stringify(articleLd)}</script>

      <article>
        <div className="relative h-[300px] md:h-[400px] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/50"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <div className="max-w-4xl mx-auto">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-background-50/90 text-primary-600 mb-3">
                {categoryLabel}
              </span>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-background-50 mb-3 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-background-200">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i> {article.readTime}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-6 py-8">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryLabel, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-6 pb-10">
          <div
            className="prose prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:mt-8 prose-headings:mb-4
              prose-h2:text-xl prose-h2:md:text-2xl prose-h2:font-bold
              prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5
              prose-strong:text-foreground-900
              [&_h2]:border-l-4 [&_h2]:border-primary-500 [&_h2]:pl-4
            "
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-6 border-t border-background-200/70">
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs font-medium rounded-full bg-secondary-50 text-secondary-700 border border-secondary-200/70"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-5 rounded-lg bg-background-100 border border-background-200/70">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                <i className="ri-user-line text-lg"></i>
              </span>
              <div>
                <div className="text-sm font-medium text-foreground-800">{article.author}</div>
                <div className="text-xs text-foreground-500">初音岛粉丝资讯站作者</div>
              </div>
            </div>
            <Link
              to={`/category/${article.category}`}
              className="px-4 py-2 text-sm font-medium text-primary-500 hover:text-primary-600 border border-primary-200 rounded-full hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              浏览更多{categoryLabel}
            </Link>
          </div>
        </div>
      </article>

      {relatedArticles.length > 0 && (
        <section className="py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
              相关文章推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedArticles.map((ra) => (
                <ArticleCard key={ra.slug} article={ra} />
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}